'use client'

import { useState } from 'react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ReferenceDot } from 'recharts'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { pct } from '@/lib/format'
import type { SandboxVisibilityPoint } from '../data/types'
import type { Engine } from '@/lib/ai/constants'

type EngineFilter = 'all' | Engine

const FILTER_OPTIONS: { id: EngineFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'google_aio', label: 'Google AI Overview' },
]

const CHART_CONFIG: ChartConfig = {
  score: {
    label: 'Visibility',
    color: 'var(--series-1)',
  },
}

interface ChartPoint {
  label: string
  score: number
  annotation?: string
  rawDate: string
  chatgpt: number
  perplexity: number
  google_aio: number
}

function buildChartData(visibility: SandboxVisibilityPoint[], filter: EngineFilter): ChartPoint[] {
  return visibility.map((point) => ({
    label: format(new Date(point.date), 'MMM d'),
    score: filter === 'all' ? point.score : (point.perEngine[filter] ?? 0),
    annotation: point.annotation,
    rawDate: point.date,
    chatgpt: point.perEngine.chatgpt ?? 0,
    perplexity: point.perEngine.perplexity ?? 0,
    google_aio: point.perEngine.google_aio ?? 0,
  }))
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ payload: ChartPoint }>
}

function VisibilityTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.[0]) return null
  const point = payload[0].payload
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2.5 shadow-md text-xs min-w-[140px]">
      <p className="font-semibold text-foreground mb-1.5">{point.label}</p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Score</span>
          <span className="font-mono tabular-nums font-medium">{pct(point.score / 100)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">ChatGPT</span>
          <span className="font-mono tabular-nums">{pct(point.chatgpt / 100)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Perplexity</span>
          <span className="font-mono tabular-nums">{pct(point.perplexity / 100)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Google AI Overview</span>
          <span className="font-mono tabular-nums">{pct(point.google_aio / 100)}</span>
        </div>
      </div>
      {point.annotation && (
        <p className="mt-2 border-t border-border pt-1.5 text-micro text-amber">
          {point.annotation}
        </p>
      )}
    </div>
  )
}

interface VisibilityPageProps {
  visibility: SandboxVisibilityPoint[]
}

export function VisibilityPage({ visibility }: VisibilityPageProps) {
  const [filter, setFilter] = useState<EngineFilter>('all')

  const chartData = buildChartData(visibility, filter)
  // The headline is the series' own last point, whatever engine the chips filter to — the page
  // prints the figure only in a tooltip, so the container carries it for the sample-number check.
  const latestVisibility = visibility.at(-1)?.score

  // Find annotated points for ReferenceDot pins
  const annotatedPoints = chartData
    .map((point, index) => ({ ...point, index }))
    .filter((p) => Boolean(p.annotation))

  return (
    <div className="space-y-3">
      {/* Engine filter chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setFilter(option.id)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs transition-colors duration-150',
              filter === option.id
                ? 'bg-foreground text-background font-medium'
                : 'bg-muted text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <ChartContainer
        config={CHART_CONFIG}
        data-latest-visibility={latestVisibility}
        className="h-[160px] w-full"
        initialDimension={{ width: 480, height: 160 }}
      >
        <AreaChart data={chartData} margin={{ top: 12, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
            tickMargin={6}
            interval={2}
          />
          <YAxis
            domain={[0, 100]}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(v: number) => pct(v / 100)}
            ticks={[0, 50, 100]}
          />
          <ChartTooltip
            content={<VisibilityTooltip />}
            cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="score"
            stroke="var(--series-1)"
            strokeWidth={2}
            fill="var(--series-1)"
            fillOpacity={0.4}
            dot={(props: { cx?: number; cy?: number; payload?: ChartPoint }) => {
              const { cx, cy, payload } = props
              if (payload?.annotation) return <g key={`dot-skip-${cx}-${cy}`} />
              return (
                <circle
                  key={`dot-${cx}-${cy}`}
                  cx={cx}
                  cy={cy}
                  r={2.5}
                  fill="var(--series-1)"
                  stroke="none"
                />
              )
            }}
            activeDot={{ fill: 'var(--series-1)', strokeWidth: 2, stroke: 'var(--card)', r: 4 }}
            connectNulls={false}
          />
          {/* Annotation pins */}
          {annotatedPoints.map((point) => (
            <ReferenceDot
              key={point.rawDate}
              x={point.label}
              y={point.score}
              r={5}
              fill="var(--amber)"
              stroke="var(--card)"
              strokeWidth={1.5}
            />
          ))}
        </AreaChart>
      </ChartContainer>

      {/* Annotation legend */}
      {annotatedPoints.length > 0 && (
        <div className="space-y-1">
          <p className="u-label">Events</p>
          {annotatedPoints.map((point) => (
            <div key={point.rawDate} className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 size-2 rounded-full bg-amber" aria-hidden="true" />
              <p className="text-label text-muted-foreground">
                <span className="font-medium text-foreground">{point.label}</span> —{' '}
                {point.annotation}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
